'use client';

import { useCallback, useMemo } from 'react';
import config from 'config';
import { defaultBasketData, ROUTES, ShoppingCartIcon } from 'constant';
import { useRouter } from 'next/navigation';
import {
  useAddPromoCodeMutation,
  useGetShoppingCartQuery
} from 'services/shoppingCartApi';

import {
  Box,
  CloseButton,
  Divider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setCartStep,
  setFulfillmentPreference
} from 'store/reducers/cartSlice';
import { openGuestModal } from 'store/reducers/guestSlice';
import { toggleCartBasket } from 'store/reducers/productsSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  formatCurrency,
  getAvailableShipment,
  getPromoErrorMessage,
  sortShopList
} from 'utils';
import { hasMixedFulfillment } from 'utils/helpers/fulfillmentHelpers';

import {
  CartBasketItem,
  LocationFlowType,
  PromoErrorType,
  ShopCartStep,
  ShoppingCartName,
  UserFulfillmentMode
} from 'types';

import { Button, Text } from 'ui';
import { CartItem } from './components';
import { ContactUsErrorModal } from '../ErrorModals';
import { PromoCode } from '../PromoCode';
import { CartEmptyView } from '../ShopCartFlow';

const {
  header: { shoppingCartType },
  shop: { isAllowGuestCheckout }
} = config;

const CartBasket = () => {
  const {
    colors: {
      palette: {
        components: { cartBasket: styles }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { user } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const { selectedPackages, selectedProducts, isPreferencesMenuEnabled } =
    useAppSelector((state) => state.cart);
  const isLogged =
    user !== null && Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const isGuestLogged = Boolean(guestInfo?.clid) && Boolean(guestInfo?.token);

  const { data = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged && !isGuestLogged
  });
  const [addPromoCode, { isError, error: errorType, reset }] =
    useAddPromoCodeMutation();
  const invoiceId = guestInfo?.invoice_id || data.details?.invoice_id || 0;

  const { isCartBasketOpen } = useAppSelector((state) => state.products);

  const onClose = () => dispatch(toggleCartBasket());

  const discounts = useMemo(
    () => (data.items ?? []).filter((item) => item.is_promo),
    [data]
  );

  const getDiscountPrice = useCallback(
    (itemId: number) =>
      discounts
        .filter((item) => item?.rel_cart_item_id === itemId)
        .reduce((a, b) => a + (b?.amount_total ?? 0), 0),
    [discounts]
  );

  const handleSetDiscount = async (promoCodeValue: string) =>
    addPromoCode({
      invoice_id: invoiceId,
      promo_code: promoCodeValue
    });

  const handleItemFulfillmentChange =
    (item: CartBasketItem) => (mode: UserFulfillmentMode) => {
      if (item?.online_fulfillment !== undefined) {
        dispatch(
          setFulfillmentPreference({
            cart_item_id: item.id,
            original_fulfillment: item.online_fulfillment,
            user_selected: mode,
            invoice_id: invoiceId
          })
        );
      }
    };

  const sortedData = useMemo(
    () =>
      sortShopList(data.items ?? []).map((item) => ({
        id: item.cart_item_id,
        name: item.name,
        size: item.size,
        img: item.img,
        oldItemPrice: item.base_price === 0 ? item.amount : item.base_price,
        newItemPrice: item.amount,
        priceWithDiscount:
          item.amount_total + getDiscountPrice(item.cart_item_id),
        totalWithAllDiscount:
          (item.base_price === 0 ? item.amount : item.base_price) * item.qty,
        amount: item.amount,
        discount: item.base_price === 0 ? item.amount : item.base_price,
        amountTotal:
          item.base_price === 0 ? item.amount : item.base_price * item.qty,
        promoDiscount: getDiscountPrice(item.cart_item_id),
        qty: item.qty,
        online_fulfillment: item.online_fulfillment
      })),
    [data, getDiscountPrice]
  );

  const isShipmentExist = useMemo(
    () => (data.items ?? []).some(getAvailableShipment),
    [data]
  );

  const onCheckout = () => {
    if (!isLogged && !isGuestLogged) {
      if (isAllowGuestCheckout) {
        if (isCartBasketOpen) {
          dispatch(toggleCartBasket());
        }
        dispatch(openGuestModal());
        return;
      }
      dispatch(setRedirectFlow(LocationFlowType.BASKET));
      onClose();
      router.push(ROUTES.login);
      return;
    }
    const path: ShopCartStep = isShipmentExist
      ? ShopCartStep.DELIVERY
      : ShopCartStep.CHECKOUT;
    dispatch(setCartStep(path));
    onClose();
    router.push(ROUTES.cart);
  };

  const onShoppingBag = () => {
    dispatch(setCartStep(ShopCartStep.DETAILS));
    router.push(ROUTES.cart);
    onClose();
  };

  const localProducts: CartBasketItem[] = useMemo(
    () =>
      (selectedProducts ?? []).map((product) => ({
        id: product.id,
        name: product.name ?? '',
        size: product.size ?? '',
        img:
          product?.primary_img_thumb_url ?? product?.primary_img_x2_url ?? '',

        oldItemPrice: product.price ?? 0,
        newItemPrice: product.price ?? 0,
        priceWithDiscount: product.price * (product?.qty ?? 1),
        totalWithAllDiscount: product.price * (product?.qty ?? 1),

        qty: product?.qty ?? 1
      })),
    [selectedProducts]
  );

  const localPackages: CartBasketItem[] = useMemo(
    () =>
      (selectedPackages ?? []).map((pack) => ({
        id: pack.id,
        name: pack.name ?? '',
        size: '',
        img: '',
        oldItemPrice: pack.price ?? 0,
        newItemPrice: pack.price ?? 0,
        priceWithDiscount: pack.price * (pack?.qty ?? 1),
        totalWithAllDiscount: pack.price * (pack?.qty ?? 1),
        qty: pack?.qty ?? 1
      })),
    [selectedPackages]
  );

  const localItemStore: CartBasketItem[] = [...localProducts, ...localPackages];
  const qtyValue = isLogged || isGuestLogged ? sortedData : localItemStore;
  const qty = qtyValue.length > 0 ? ` (${qtyValue.length})` : '';
  const localSubtotal = localItemStore.reduce(
    (acc, item) => acc + item.priceWithDiscount,
    0
  );

  // Use helper function to detect mixed fulfillment
  const showMixedFulfillmentTag = hasMixedFulfillment(data.items || []);

  const subtotal =
    isLogged || isGuestLogged
      ? data.details?.total_items_cost ?? 0
      : localSubtotal;

  const totalDiscount = (data.items ?? [])
    .filter((item) => Boolean(item.is_promo))
    .reduce((acc, item) => acc + (item?.amount_total ?? 0), 0);

  const isDiscount = totalDiscount < 0;

  return (
    <>
      <Drawer
        isOpen={isCartBasketOpen}
        placement='right'
        onClose={onClose}
        size='lg'
      >
        <DrawerOverlay />
        <DrawerContent
          w={{ base: '100%', md: '600px' }}
          maxW={{ base: '100%', md: '600px' }}
          h='auto'
          bottom='auto !important'
        >
          <VStack
            gap={0}
            px='20px'
            w='100%'
            bg={styles.containerBg}
            overflowY='auto'
            overflowX='hidden'
          >
            <Flex justifyContent='space-between' alignItems='center' w='full'>
              <Box w='12px' />
              <Text py={5} fontSize='21px' color={styles.textColor}>
                {`Shopping ${shoppingCartType}${qty}`}
              </Text>
              <CloseButton
                onClick={onClose}
                color={styles.textColor}
                w='auto'
                _hover={{
                  bg: 'transparent',
                  opacity: 0.7
                }}
              />
            </Flex>

            {qtyValue.length === 0 ? (
              <CartEmptyView
                mb={{ base: '24px', md: '32px' }}
                py={{ base: '24px', md: '32px' }}
                overflow='hidden'
                Icon={ShoppingCartIcon[shoppingCartType as ShoppingCartName]}
                type={shoppingCartType}
                onClick={onClose}
              />
            ) : (
              <>
                {isDiscount && (
                  <Flex
                    justifyContent='space-between'
                    alignItems='center'
                    w='full'
                    pt={{ base: '15px', sm: '20px' }}
                    px={{ base: '8px', md: '10px' }}
                    borderColor={styles.borderColor}
                    borderTopWidth={2}
                  >
                    <Text
                      fontSize={{ base: '18px', md: '19px', lg: '21px' }}
                      color={styles.textColor}
                      textTransform='uppercase'
                    >
                      Discounts
                    </Text>
                    <Text
                      fontSize={{ base: '18px', md: '20px', lg: '22px' }}
                      color={styles.textColor}
                      textTransform='uppercase'
                    >
                      {formatCurrency(totalDiscount)}
                    </Text>
                  </Flex>
                )}
                <Flex
                  justifyContent='space-between'
                  alignItems='center'
                  w='full'
                  py={{ base: '15px', sm: '20px' }}
                  px={{ base: '8px', md: '10px' }}
                  borderColor={styles.borderColor}
                  borderTopWidth={isDiscount ? 0 : 2}
                  borderBottomWidth={2}
                >
                  <Text
                    fontSize={{ base: '18px', md: '19px', lg: '24px' }}
                    color={styles.textColor}
                    textTransform='uppercase'
                  >
                    Subtotal
                  </Text>
                  <Text
                    fontSize={{ base: '25px', md: '28px', lg: '32px' }}
                    color={styles.textColor}
                    textTransform='uppercase'
                  >
                    {formatCurrency(subtotal)}
                  </Text>
                </Flex>
                <Flex
                  flexDirection='column'
                  overflowY='scroll'
                  overflowX='hidden'
                  mt='8.5px'
                  w='full'
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '6px',

                      '&-track': {
                        width: '8px'
                      },
                      '&-thumb': {
                        background: styles.borderColor,
                        borderRadius: '24px'
                      }
                    }
                  }}
                >
                  <VStack gap='5px' w='full'>
                    {qtyValue.length ? (
                      qtyValue.map((item) => (
                        <CartItem
                          key={item.id}
                          onFulfillmentChange={handleItemFulfillmentChange(
                            item
                          )}
                          isShowTag={showMixedFulfillmentTag}
                          isTagDisabled={!isPreferencesMenuEnabled}
                          {...item}
                        />
                      ))
                    ) : (
                      <VStack height={260} justifyContent='center'>
                        <Text
                          fontSize={{ base: '18px', md: '19px', lg: '21px' }}
                          color={styles.borderColor}
                          textTransform='uppercase'
                        >
                          Cart is empty
                        </Text>
                      </VStack>
                    )}
                  </VStack>
                </Flex>
                <VStack w='100%' gap='0' pb='30px'>
                  {(isLogged || isGuestLogged) && (
                    <>
                      <Divider
                        borderBottomWidth='2px'
                        borderColor={styles.borderColor}
                        mt='12px'
                        mb='10px'
                      />
                      <PromoCode
                        my='0'
                        onSaveDiscount={handleSetDiscount}
                        isError={isError}
                        onClearError={reset}
                        title='Promo Code'
                        variant='light'
                        inputProps={{
                          width: { base: '100%', md: '240px' }
                        }}
                      />
                    </>
                  )}
                  <Divider
                    borderBottomWidth='2px'
                    borderColor={styles.borderColor}
                    my='10px'
                  />
                  <Button
                    textTransform='uppercase'
                    variant='solid'
                    h='45px'
                    w='356px'
                    mt='20px'
                    isDisabled={!sortedData.length && !isAllowGuestCheckout}
                    onClick={onCheckout}
                  >
                    Proceed to checkout
                  </Button>
                  <Button
                    textTransform='uppercase'
                    variant='link'
                    colorScheme='black'
                    onClick={onShoppingBag}
                    width='auto'
                    mt='20px'
                    _focus={{
                      boxShadow: 'none'
                    }}
                    _focusVisible={{
                      boxShadow: 'none'
                    }}
                  >
                    {`View shopping ${shoppingCartType}`}
                  </Button>
                </VStack>
              </>
            )}
          </VStack>
        </DrawerContent>
      </Drawer>

      {(() => {
        const promoErrorType =
          typeof errorType === 'number'
            ? (errorType as PromoErrorType)
            : PromoErrorType.NO_ERROR;
        return (
          <ContactUsErrorModal
            errorText={getPromoErrorMessage(promoErrorType, false)}
            onClearHandler={reset}
          />
        );
      })()}
    </>
  );
};

export default CartBasket;
